import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {
    ALERT_SNOOZE_PERIOD_FOREVER,
    ALERT_SNOOZE_PERIOD_ONE_WEEK,
    ALERT_SNOOZE_PERIOD_THIS_MONTH,
    ALERT_SNOOZE_PERIOD_TODAY,
} from '../../periods';

// Los cuatro plazos son los que acepta `AlertSnooze` en el backend (`alert-snooze-all` los valida).
// El default es una semana, el mismo que el popover de alertas de la 2.1: el caso central es "lo
// estoy trabajando y no quiero el recordatorio", no "por hoy" — que era lo único que hacía la
// campanita antes de la card ND0fME30, y obligaba a volver a silenciar cada mañana.
export const SNOOZE_ALL_PERIODS = [
    ALERT_SNOOZE_PERIOD_TODAY,
    ALERT_SNOOZE_PERIOD_ONE_WEEK,
    ALERT_SNOOZE_PERIOD_THIS_MONTH,
    ALERT_SNOOZE_PERIOD_FOREVER,
];
export const DEFAULT_SNOOZE_ALL_PERIOD = ALERT_SNOOZE_PERIOD_ONE_WEEK;

/**
 * Hasta cuándo callaría cada plazo, para poder decirlo ANTES de elegir.
 *
 * ⚠️ Espeja `AlertSnooze._define_snooze_till` del backend (hoy: fin del día · +7 días exactos · fin
 * del último día del mes · sin vencimiento). Si allá cambia el cálculo, cambia acá — y al revés, un
 * plazo nuevo en `SNOOZE_ALL_PERIODS` sin su rama devuelve `null` y se lee como «sin vencimiento».
 * Lo que se ve DESPUÉS de silenciar no sale de acá sino del `snoozed_until` que contesta el backend,
 * así que una divergencia se nota en el tooltip y no queda escondida.
 *
 * El backend calcula en la zona del usuario y esto en la del navegador: pueden diferir en el borde
 * del día, y por eso acá se muestra una fecha y no una hora exacta (salvo «por hoy»).
 */
export function snoozeEndsAt(period, now = new Date()) {
    const end = new Date(now.getTime());
    if (period === ALERT_SNOOZE_PERIOD_TODAY) {
        end.setHours(23, 59, 59, 999);
        return end;
    }
    if (period === ALERT_SNOOZE_PERIOD_ONE_WEEK) {
        end.setDate(end.getDate() + 7);
        return end;
    }
    if (period === ALERT_SNOOZE_PERIOD_THIS_MONTH) {
        // Día 0 del mes siguiente = el último del actual.
        return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    return null;   // FOREVER, y cualquier plazo que todavía no tenga rama
}

/**
 * "¿Por cuánto tiempo silencio las alertas de esta línea?" — el modal de la campanita del Tablero.
 *
 * Sólo elige el plazo y lo devuelve en `onConfirm(period)`: quién llama al backend y qué pasa
 * después es del botón. Montarlo únicamente mientras está abierto (`{isOpen && <Modal/>}`) es lo
 * que devuelve el plazo al default en cada apertura, sin un efecto que lo resetee.
 */
export function SnoozeAllAlertsModal(props) {
    const {lineName, isSaving, onConfirm, onCancel} = props;
    const {t, i18n} = useTranslation();
    const [period, setPeriod] = useState(DEFAULT_SNOOZE_ALL_PERIOD);

    const untilLabel = (option) => {
        const endsAt = snoozeEndsAt(option);
        if (!endsAt) return t('dashboard.snooze_all.until_never');
        if (option === ALERT_SNOOZE_PERIOD_TODAY) {
            return t('dashboard.snooze_all.until_time', {
                time: endsAt.toLocaleTimeString(i18n.language || undefined,
                                                {hour: '2-digit', minute: '2-digit'}),
            });
        }
        return t('dashboard.snooze_all.until_date', {
            date: endsAt.toLocaleDateString(i18n.language || undefined, {day: 'numeric', month: 'long'}),
        });
    };

    return (
        <Modal isOpen toggle={onCancel} className="snooze-all-modal">
            <ModalHeader toggle={onCancel}>
                {t('dashboard.snooze_all.modal_title', {name: lineName, interpolation: {escapeValue: false}})}
            </ModalHeader>
            <ModalBody>
                <div className="snooze-all">
                    <p className="snooze-all__sub">{t('dashboard.snooze_all.modal_sub')}</p>
                    {SNOOZE_ALL_PERIODS.map((option) => (
                        <label key={option} className={`snooze-all__op${option === period ? ' -on' : ''}`}>
                            <input type="radio" name="snooze-all-period" value={option}
                                   checked={option === period}
                                   onChange={() => setPeriod(option)}/>
                            <span className="snooze-all__content">
                                <span className="snooze-all__t">
                                    {t(`dashboard.snooze_all.period_${option}`)}
                                </span>
                                <span className="snooze-all__d">
                                    {t(`dashboard.snooze_all.period_desc_${option}`)}
                                </span>
                            </span>
                            <span className="snooze-all__until">{untilLabel(option)}</span>
                        </label>
                    ))}
                    <p className="snooze-all__note">
                        <strong>{t('dashboard.snooze_all.note_title')}</strong>
                        {` ${t('dashboard.snooze_all.note')}`}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={onCancel} disabled={isSaving}>
                    {t('dashboard.snooze_all.cancel')}
                </button>
                <button type="button" className="btn" onClick={() => onConfirm(period)} disabled={isSaving}>
                    {t('dashboard.snooze_all.confirm')}
                </button>
            </ModalFooter>
        </Modal>
    );
}

export default SnoozeAllAlertsModal;
